import {
  Output,
  Mp4OutputFormat,
  BufferTarget,
  CanvasSource,
  AudioBufferSource,
  QUALITY_HIGH,
  QUALITY_MEDIUM,
  getFirstEncodableVideoCodec,
  getFirstEncodableAudioCodec,
  type VideoCodec,
  type AudioCodec,
} from 'mediabunny';
import type { Visualizer } from '../viz/types';
import type { LrcDocument } from '../lyrics/lrc';
import type { ExportPreset, RecorderResult } from './recorder';
import {
  analyzeAt,
  audioBufferToMono,
  createAnalyzerState,
} from '../audio/analyze';
import { drawLyricsOverlay } from '../viz/lyricsOverlay';

export type OfflineProgress = {
  phase: 'video' | 'audio' | 'finalize';
  frame: number;
  totalFrames: number;
  ratio: number;
  elapsedMs: number;
};

export type LyricsMode = 'karaoke' | 'hud' | 'off';

export type OfflineRenderOptions = {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  audioBuffer: AudioBuffer;
  preset: ExportPreset;
  viz: Visualizer;
  title: string;
  artist: string;
  cover: HTMLImageElement | null;
  sensitivity: number;
  lyrics?: LrcDocument | null;
  lyricsMode?: LyricsMode;
  lyricsOffsetSec?: number;
  /** Include REC badge in baked frames (default false for offline). */
  showRecBadge?: boolean;
  signal?: AbortSignal;
  onProgress?: (p: OfflineProgress) => void;
};

export type EncodeCapabilities = {
  offlineMp4: boolean;
  videoCodec: VideoCodec | null;
  audioCodec: AudioCodec | null;
  label: string;
};

export async function probeEncodeCapabilities(): Promise<EncodeCapabilities> {
  const hasWE =
    typeof VideoEncoder !== 'undefined' && typeof AudioEncoder !== 'undefined';
  if (!hasWE) {
    return {
      offlineMp4: false,
      videoCodec: null,
      audioCodec: null,
      label: 'Realtime MediaRecorder only (no WebCodecs)',
    };
  }
  const videoCodec = await getFirstEncodableVideoCodec(['avc', 'vp9', 'av1']);
  const audioCodec = await getFirstEncodableAudioCodec(['aac', 'opus']);
  const offlineMp4 = !!(videoCodec && audioCodec);
  const label = offlineMp4
    ? `Offline MP4 · ${videoCodec}+${audioCodec}`
    : videoCodec
      ? `Partial WebCodecs (${videoCodec}, no audio codec)`
      : 'Realtime MediaRecorder fallback';
  return { offlineMp4, videoCodec, audioCodec, label };
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    const err = new Error('Export cancelled');
    err.name = 'AbortError';
    throw err;
  }
}

function qualityForPreset(p: ExportPreset) {
  const pixels = p.width * p.height * p.fps;
  return pixels >= 1920 * 1080 * 50 ? QUALITY_HIGH : QUALITY_MEDIUM;
}

/**
 * Faster-than-realtime offline render:
 * PCM → per-frame FFT bands → canvas draw → WebCodecs via Mediabunny → MP4 in memory.
 */
export async function renderOfflineMp4(opts: OfflineRenderOptions): Promise<RecorderResult> {
  const caps = await probeEncodeCapabilities();
  if (!caps.offlineMp4 || !caps.videoCodec || !caps.audioCodec) {
    throw new Error('Offline MP4 not supported in this browser (need WebCodecs video+audio).');
  }

  const {
    canvas,
    ctx,
    audioBuffer,
    preset,
    viz,
    title,
    artist,
    cover,
    sensitivity,
    lyrics = null,
    lyricsMode = 'karaoke',
    lyricsOffsetSec = 0,
    showRecBadge = false,
    signal,
    onProgress,
  } = opts;

  const started = performance.now();
  const duration = audioBuffer.duration;
  if (!(duration > 0.05)) throw new Error('Audio too short');

  canvas.width = preset.width;
  canvas.height = preset.height;

  const fps = preset.fps;
  const frameDur = 1 / fps;
  const totalFrames = Math.max(1, Math.ceil(duration * fps));
  const mono = audioBufferToMono(audioBuffer);
  const state = createAnalyzerState();

  const target = new BufferTarget();
  const output = new Output({
    format: new Mp4OutputFormat({ fastStart: 'in-memory' }),
    target,
  });

  const videoSource = new CanvasSource(canvas, {
    codec: caps.videoCodec,
    bitrate: qualityForPreset(preset),
    keyFrameInterval: 2,
    bitrateMode: 'variable',
  });
  output.addVideoTrack(videoSource, { frameRate: fps });

  const audioSource = new AudioBufferSource({
    codec: caps.audioCodec,
    bitrate: 192_000,
  });
  output.addAudioTrack(audioSource);

  await output.start();

  try {
    for (let i = 0; i < totalFrames; i++) {
      throwIfAborted(signal);
      const t = i * frameDur;
      if (t > duration) break;

      const analysis = analyzeAt(mono, audioBuffer.sampleRate, t, sensitivity, state);
      viz.draw({
        ctx,
        w: canvas.width,
        h: canvas.height,
        t,
        bands: analysis.bands,
        freq: analysis.freq,
        wave: analysis.wave,
        title,
        artist,
        cover,
        recording: showRecBadge,
      });

      drawLyricsOverlay({
        ctx,
        w: canvas.width,
        h: canvas.height,
        audioTime: t,
        doc: lyrics,
        userOffsetSec: lyricsOffsetSec,
        bands: analysis.bands,
        mode: lyricsMode,
      });

      await videoSource.add(t, frameDur);

      if (i % 3 === 0 || i === totalFrames - 1) {
        const elapsedMs = performance.now() - started;
        onProgress?.({
          phase: 'video',
          frame: i + 1,
          totalFrames,
          ratio: ((i + 1) / totalFrames) * 0.85,
          elapsedMs,
        });
        await new Promise<void>((r) => setTimeout(r, 0));
      }
    }

    throwIfAborted(signal);
    onProgress?.({
      phase: 'audio',
      frame: totalFrames,
      totalFrames,
      ratio: 0.9,
      elapsedMs: performance.now() - started,
    });

    await audioSource.add(audioBuffer);

    throwIfAborted(signal);
    onProgress?.({
      phase: 'finalize',
      frame: totalFrames,
      totalFrames,
      ratio: 0.97,
      elapsedMs: performance.now() - started,
    });

    await output.finalize();
  } catch (e) {
    try {
      await output.cancel();
    } catch {
      /* ignore */
    }
    throw e;
  }

  const buf = target.buffer;
  if (!buf) throw new Error('MP4 mux produced empty buffer');

  const blob = new Blob([new Uint8Array(buf)], { type: 'video/mp4' });
  const elapsedMs = performance.now() - started;
  onProgress?.({
    phase: 'finalize',
    frame: totalFrames,
    totalFrames,
    ratio: 1,
    elapsedMs,
  });

  return {
    blob,
    mimeType: 'video/mp4',
    extension: 'mp4',
    durationMs: elapsedMs,
  };
}
